import React from 'react'
import { Control, FieldErrors, UseFormRegister, UseFormSetValue, UseFormWatch } from 'react-hook-form'
import { t } from 'ttag'
import { activityTypes, FormData } from '../../ChallengeAdmin'
import { FormGroup, ListItem, ListItemIcon, ListItemText } from '@material-ui/core'
import YumFormSwitch from '../../../../components/YumFormSwitch/YumFormSwitch'
import { CustomTypeCategory } from '../../../../services/CustomTypes'
import Typography from '@material-ui/core/Typography'
import ChallengePoints from '../ChallengePoints/ChallengePoints'

import { ReactComponent as IndividualActiveIcon } from '../../../../assets/v3/icons/individual-active.svg'
import { ReactComponent as IndividualInactiveIcon } from '../../../../assets/v3/icons/individual-inactive.svg'

import { ReactComponent as GroupActiveIcon } from '../../../../assets/v3/icons/group-active.svg'
import { ReactComponent as GroupInactiveIcon } from '../../../../assets/v3/icons/group-inactive.svg'

import { ReactComponent as CountryActiveIcon } from '../../../../assets/v3/icons/country-active.svg'
import { ReactComponent as CountryInactiveIcon } from '../../../../assets/v3/icons/country-inactive.svg'

import { ReactComponent as ChatActiveIcon } from '../../../../assets/v3/icons/chat-active.svg'
import { ReactComponent as ChatInactiveIcon } from '../../../../assets/v3/icons/chat-inactive.svg'

import { ReactComponent as ManualActiveIcon } from '../../../../assets/v3/icons/manual-active.svg'
import { ReactComponent as ManualInactiveIcon } from '../../../../assets/v3/icons/manual-inactive.svg'

import { ReactComponent as RoundDailyPointsActiveIcon } from '../../../../assets/v3/icons/round-daily-points-active.svg'
import { ReactComponent as RoundDailyPointsInactiveIcon } from '../../../../assets/v3/icons/round-daily-points-inactive.svg'

import { ReactComponent as AutoJoinUsersActiveIcon } from '../../../../assets/v3/icons/auto-join-users-active.svg'
import { ReactComponent as AutoJoinUsersInactiveIcon } from '../../../../assets/v3/icons/auto-join-users-inactive.svg'

interface ChallengeStepPeriodProps {
  register: UseFormRegister<FormData>
  control: Control<FormData>
  watch: UseFormWatch<FormData>
  setValue: UseFormSetValue<FormData>
  errors: FieldErrors<FormData>
  activityType: CustomTypeCategory
  isUserCreatedChallenge: boolean
}

export default function ChallengeStepConfiguration(props: ChallengeStepPeriodProps) {
  const { register, control, errors, watch, setValue, activityType, isUserCreatedChallenge } = props

  const individualLeaderboard = watch('individualLeaderboard')
  const groupLeaderboard = watch('groupLeaderboard')
  const countryLeaderboard = watch('countryLeaderboard')
  const onlyOneTabShown = [individualLeaderboard, groupLeaderboard, countryLeaderboard].filter((v) => v).length === 1
  const chatEnabled = watch('chatEnabled')
  const manualEntryEnabled = watch('manualEntryEnabled')
  const roundDailyPoints = watch('roundDailyPoints')
  const autoJoinEnabled = watch('autoJoinEnabled')

  const showManualEntryOption = !!activityTypes.find(
    ({ value, usesIntegration }) => value === activityType && usesIntegration,
  )
  const showRoundDailyPointsOption = activityType === CustomTypeCategory.POINTS

  const pointsConfig = (
    <>
      <Typography
        variant="subtitle2"
        style={{ fontWeight: 500, fontSize: 16, marginTop: 21, marginBottom: 11 }}
      >{t`Wellness points`}</Typography>
      <ChallengePoints control={control} watch={watch} register={register} setValue={setValue} errors={errors} />
      <Typography
        variant="subtitle2"
        style={{ fontWeight: 500, fontSize: 16, marginTop: 21, marginBottom: 11 }}
      >{t`Other`}</Typography>
    </>
  )

  const futureTranslations = () => {
    t`Auto-join users`
    t`Max manual entry days`
    return
  }

  return (
    <>
      {activityType === CustomTypeCategory.POINTS && pointsConfig}
      <FormGroup>
        <ListItem
          role={undefined}
          dense
          button
          onClick={() =>
            !((onlyOneTabShown && individualLeaderboard) || isUserCreatedChallenge) &&
            setValue('individualLeaderboard', !individualLeaderboard)
          }
        >
          <ListItemIcon>{individualLeaderboard ? <IndividualActiveIcon /> : <IndividualInactiveIcon />}</ListItemIcon>
          <ListItemText primary={t`Individual leaderboard`} />
          <ListItemIcon>
            <YumFormSwitch
              control={control}
              name="individualLeaderboard"
              disabled={(onlyOneTabShown && individualLeaderboard) || isUserCreatedChallenge}
            />
          </ListItemIcon>
        </ListItem>
        <ListItem
          role={undefined}
          dense
          button
          onClick={() =>
            !((onlyOneTabShown && groupLeaderboard) || isUserCreatedChallenge) &&
            setValue('groupLeaderboard', !groupLeaderboard)
          }
        >
          <ListItemIcon>{groupLeaderboard ? <GroupActiveIcon /> : <GroupInactiveIcon />}</ListItemIcon>
          <ListItemText primary={t`Group leaderboard`} />
          <ListItemIcon>
            <YumFormSwitch
              control={control}
              name="groupLeaderboard"
              disabled={(onlyOneTabShown && groupLeaderboard) || isUserCreatedChallenge}
            />
          </ListItemIcon>
        </ListItem>
        <ListItem
          role={undefined}
          dense
          button
          onClick={() =>
            !((onlyOneTabShown && countryLeaderboard) || isUserCreatedChallenge) &&
            setValue('countryLeaderboard', !countryLeaderboard)
          }
        >
          <ListItemIcon>{countryLeaderboard ? <CountryActiveIcon /> : <CountryInactiveIcon />}</ListItemIcon>
          <ListItemText primary={t`Country leaderboard`} />
          <ListItemIcon>
            <YumFormSwitch
              control={control}
              name="countryLeaderboard"
              disabled={(onlyOneTabShown && countryLeaderboard) || isUserCreatedChallenge}
            />
          </ListItemIcon>
        </ListItem>
        <ListItem role={undefined} dense button onClick={() => setValue('chatEnabled', !chatEnabled)}>
          <ListItemIcon>{chatEnabled ? <ChatActiveIcon /> : <ChatInactiveIcon />}</ListItemIcon>
          <ListItemText primary={t`Chat`} />
          <ListItemIcon>
            <YumFormSwitch control={control} name="chatEnabled" />
          </ListItemIcon>
        </ListItem>
        {showManualEntryOption && (
          <ListItem role={undefined} dense button onClick={() => setValue('manualEntryEnabled', !manualEntryEnabled)}>
            <ListItemIcon>{manualEntryEnabled ? <ManualActiveIcon /> : <ManualInactiveIcon />}</ListItemIcon>
            <ListItemText primary={t`Manual entry`} />
            <ListItemIcon>
              <YumFormSwitch control={control} name="manualEntryEnabled" />
            </ListItemIcon>
          </ListItem>
        )}
        {showRoundDailyPointsOption && (
          <ListItem role={undefined} dense button onClick={() => setValue('roundDailyPoints', !roundDailyPoints)}>
            <ListItemIcon>
              {roundDailyPoints ? <RoundDailyPointsActiveIcon /> : <RoundDailyPointsInactiveIcon />}
            </ListItemIcon>
            <ListItemText primary={t`Rounding of daily points`} />
            <ListItemIcon>
              <YumFormSwitch control={control} name="roundDailyPoints" />
            </ListItemIcon>
          </ListItem>
        )}
        {/*<ListItem role={undefined} dense button onClick={() => setValue('autoJoinEnabled', !autoJoinEnabled)}>
          <ListItemIcon>{autoJoinEnabled ? <AutoJoinUsersActiveIcon /> : <AutoJoinUsersInactiveIcon />}</ListItemIcon>
          <ListItemText primary={t`Auto-join users`} />
          <ListItemIcon>
            <YumFormSwitch control={control} name="autoJoinEnabled" />
          </ListItemIcon>
        </ListItem>*/}
      </FormGroup>
    </>
  )
}
